// ============================================================================
// useFreelancerHours.js — CRUD hook for freelancer_monthly_hours
// ============================================================================
// Drives the Freelancer Management page. Reads the row set for a given month
// (one row per freelancer-flagged user), exposes snapshot / approve / unapprove
// actions, and pre-fills logged hours from time_entries via the SQL RPC.
//
// Conventions:
//   DB: snake_case  |  UI: camelCase
//   All writes go through requireSession() for RLS compliance
//   Mutations return { success, error? }
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError } from './useSupabase';

const requireSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error('Authentication error: ' + error.message);
  if (!session?.user) throw new Error('Please sign in to perform this action.');
  return session.user.id;
};

function normalizeRow(row) {
  if (!row) return null;
  return {
    id:              row.id,
    userId:          row.user_id,
    month:           row.month,
    loggedHours:     row.logged_hours == null ? 0 : Number(row.logged_hours),
    contractedHours: row.contracted_hours == null ? null : Number(row.contracted_hours),
    status:          row.status || 'pending',
    approvedBy:      row.approved_by || null,
    approvedAt:      row.approved_at || null,
    notes:           row.notes || '',
    createdAt:       row.created_at,
    updatedAt:       row.updated_at,
  };
}

function monthDate(year, month) {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

// ============================================================================
// MAIN HOOK — per-month or per-year rows for the page
// ============================================================================
// Pass month=null to fetch every row in the year (year view); otherwise the
// hook fetches just that month. All action helpers accept an optional
// monthOverride (1–12) so the year view can act on a specific month even
// when the hook is in year mode.
// ============================================================================
export function useFreelancerHours(year, month) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const monthStr = useMemo(
    () => (year && month ? monthDate(year, month) : null),
    [year, month]
  );

  const yearRange = useMemo(() => {
    if (!year || month) return null;
    return { from: `${year}-01-01`, to: `${year}-12-01` };
  }, [year, month]);

  const resolveMonthStr = useCallback((monthOverride) => {
    if (monthOverride && year) return monthDate(year, monthOverride);
    return monthStr;
  }, [monthStr, year]);

  const fetchRows = useCallback(async () => {
    if (!monthStr && !yearRange) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let query = supabase.from('freelancer_monthly_hours').select('*');
      if (monthStr) {
        query = query.eq('month', monthStr);
      } else if (yearRange) {
        query = query.gte('month', yearRange.from).lte('month', yearRange.to);
      }
      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setRows((data || []).map(normalizeRow));
    } catch (err) {
      const msg = formatSupabaseError(err, { action: 'fetch', entity: 'freelancer hours' });
      console.error('[useFreelancerHours] fetch error:', err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [monthStr, yearRange]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const snapshotMonth = useCallback(async (monthOverride) => {
    const target = resolveMonthStr(monthOverride);
    if (!target) return { success: false, error: 'No month selected' };
    try {
      await requireSession();
      const { error: rpcError } = await supabase.rpc('freelancer_snapshot_logged_hours', {
        p_month: target,
      });
      if (rpcError) throw rpcError;
      await fetchRows();
      return { success: true };
    } catch (err) {
      const msg = formatSupabaseError(err, { action: 'snapshot', entity: 'freelancer hours' });
      return { success: false, error: msg };
    }
  }, [resolveMonthStr, fetchRows]);

  // Snapshot every month in the current year (year view convenience). Calls
  // the per-month RPC for each month in parallel.
  const snapshotYear = useCallback(async () => {
    if (!year) return { success: false, error: 'No year selected' };
    try {
      await requireSession();
      const months = Array.from({ length: 12 }, (_, i) => monthDate(year, i + 1));
      const results = await Promise.all(
        months.map(m => supabase.rpc('freelancer_snapshot_logged_hours', { p_month: m }))
      );
      const firstError = results.find(r => r.error);
      if (firstError) throw firstError.error;
      await fetchRows();
      return { success: true };
    } catch (err) {
      const msg = formatSupabaseError(err, { action: 'snapshot', entity: 'freelancer hours (year)' });
      return { success: false, error: msg };
    }
  }, [year, fetchRows]);

  const setContractedHours = useCallback(async (userId, hours, monthOverride) => {
    const target = resolveMonthStr(monthOverride);
    if (!target) return { success: false, error: 'No month selected' };
    try {
      await requireSession();
      const { data, error: rpcError } = await supabase.rpc('freelancer_set_contracted_hours', {
        p_user_id:          userId,
        p_month:            target,
        p_contracted_hours: hours,
      });
      if (rpcError) throw rpcError;
      if (data && data.success === false) {
        return { success: false, error: data.error || 'Update failed.' };
      }
      await fetchRows();
      return { success: true };
    } catch (err) {
      const msg = formatSupabaseError(err, { action: 'update', entity: 'freelancer hours' });
      return { success: false, error: msg };
    }
  }, [resolveMonthStr, fetchRows]);

  const approve = useCallback(async (userId, hours, notes, monthOverride) => {
    const target = resolveMonthStr(monthOverride);
    if (!target) return { success: false, error: 'No month selected' };
    try {
      await requireSession();
      const { data, error: rpcError } = await supabase.rpc('freelancer_approve_month', {
        p_user_id:          userId,
        p_month:            target,
        p_contracted_hours: hours == null ? null : hours,
        p_notes:            notes || null,
      });
      if (rpcError) throw rpcError;
      if (data && data.success === false) {
        return { success: false, error: data.error || 'Approve failed.' };
      }
      await fetchRows();
      return { success: true };
    } catch (err) {
      const msg = formatSupabaseError(err, { action: 'approve', entity: 'freelancer hours' });
      return { success: false, error: msg };
    }
  }, [resolveMonthStr, fetchRows]);

  const unapprove = useCallback(async (userId, monthOverride) => {
    const target = resolveMonthStr(monthOverride);
    if (!target) return { success: false, error: 'No month selected' };
    try {
      await requireSession();
      const { data, error: rpcError } = await supabase.rpc('freelancer_unapprove_month', {
        p_user_id: userId,
        p_month:   target,
      });
      if (rpcError) throw rpcError;
      if (data && data.success === false) {
        return { success: false, error: data.error || 'Unapprove failed.' };
      }
      await fetchRows();
      return { success: true };
    } catch (err) {
      const msg = formatSupabaseError(err, { action: 'unapprove', entity: 'freelancer hours' });
      return { success: false, error: msg };
    }
  }, [resolveMonthStr, fetchRows]);

  return {
    rows,
    loading,
    error,
    monthStr,
    refetch: fetchRows,
    snapshotMonth,
    snapshotYear,
    setContractedHours,
    approve,
    unapprove,
  };
}

// ============================================================================
// useApprovedFreelancerHoursMap — for cost-math read sites
// ============================================================================
// Loads every approved freelancer_monthly_hours row and exposes a Map keyed by
// `${userId}|${YYYY-MM}` → contractedHours. Used in App.js cost helpers to
// override role-history values for freelancer months.
//
// Also exposes a "pending" map so callers can detect freelancer months that
// have not yet been approved (fall back to logged hours + show a warning chip).
// ============================================================================
export function useApprovedFreelancerHoursMap() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('freelancer_monthly_hours')
        .select('user_id, month, logged_hours, contracted_hours, status');
      if (fetchError) throw fetchError;
      setRows(data || []);
    } catch (err) {
      console.error('[useApprovedFreelancerHoursMap] fetch error:', err);
      setError(err.message || 'Failed to load freelancer hours');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const { approvedMap, pendingMap } = useMemo(() => {
    const approved = new Map();
    const pending  = new Map();
    for (const r of rows) {
      const ym = r.month ? String(r.month).slice(0, 7) : null;
      if (!ym || r.user_id == null) continue;
      const key = `${String(r.user_id)}|${ym}`;
      if (r.status === 'approved' && r.contracted_hours != null) {
        approved.set(key, Number(r.contracted_hours));
      } else if (r.status === 'pending') {
        pending.set(key, Number(r.logged_hours || 0));
      }
    }
    return { approvedMap: approved, pendingMap: pending };
  }, [rows]);

  return { approvedMap, pendingMap, loading, error, refetch: fetchAll };
}
